<template>
  <Modal
    class="roles"
    v-if="modals.roles && nonTravelers >= 5"
    @close="toggleModal('roles')"
  >
    <h3>Pick the characters for {{ nonTravelers }} players</h3>
    <ul
      class="tokens"
      v-for="(teamRoles, team) in visibleRoleSelection"
      :key="team"
    >
      <li class="team-tally" :class="'team-' + team">
        {{ pickedIn(teamRoles) }}/{{ neededFor(team) }}
      </li>
      <li
        v-for="role in teamRoles"
        :key="role.id"
        class="coin"
        :class="['team-' + role.team, { picked: role.selected }]"
        @click="toggleRole(role)"
      >
        <Token :role="role" />
        <font-awesome-icon
          v-if="role.setup"
          class="setup-flag"
          icon="exclamation-triangle"
        />
        <div class="buttons" v-if="allowDup">
          <font-awesome-icon
            icon="minus-circle"
            @click.stop="nudgeRole(role, -1)"
          />
          <span class="count">{{
            role.selected > 1 ? "x" + role.selected : ""
          }}</span>
          <font-awesome-icon
            icon="plus-circle"
            @click.stop="nudgeRole(role, 1)"
          />
        </div>
      </li>
    </ul>
    <div v-if="setupInPlay" class="setup-note">
      <font-awesome-icon icon="exclamation-triangle" />
      <span class="setup-tip">
        Some of these characters change how the game is set up. The random pick
        doesn't allow for that, so adjust it by hand.
      </span>
    </div>
    <label class="multiple" :class="{ checked: allowDup }">
      <font-awesome-icon :icon="allowDup ? 'check-square' : 'square'" />
      <input type="checkbox" name="allow-multiple" v-model="allowDup" />
      Allow duplicates
    </label>
    <div class="button-group">
      <div
        class="button"
        :class="{ disabled: !selectedRoles || selectedRoles > nonTravelers }"
        @click="assignRoles"
      >
        <!-- FT-1242: FA `people-arrows` stood down — assigning to seats IS
             the deal, and the deal already has its hand (ui-deal.png). -->
        <img class="rm-deal-mark" :src="uiDeal" alt="" />
        Deal {{ selectedRoles }} to the seats at random
      </div>
      <div class="button" @click="selectRandomRoles">
        <font-awesome-icon icon="random" />
        Shuffle the pick
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import Modal from "./Modal";
import Token from "../Token";
import gameJSON from "./../../game";
// FT-946: THE shared rule for "is this role already in play" — RoleDrawer's
// own build-panel list reads the same function, so this picker cannot list
// (and randomly re-deal) a role a seat already holds while Duplicates is off.
import { placedCount } from "../../golem/duplicates";
// FT-1242: the assign button wears the deal's own hand (RoleActions' mark).
import uiDeal from "../../assets/ui-deal.png";
// FT-1387: a refused Assign click speaks instead of the greyed button
// swallowing it in silence.
import { flashHint } from "../../golem/hint";

/** Any one member of a non-empty list, chosen evenly. */
const anyOf = (list) => list[Math.floor(Math.random() * list.length)];

export default {
  components: { Token, Modal },
  data() {
    return {
      uiDeal,
      // team → that team's roles, in script order; each role carries its
      // own `selected` count
      roleSelection: {},
      game: gameJSON,
      // FT-1394: the auto-pick used to run ONCE, at page load (this component
      // mounts immediately — the v-if sits on the inner Modal). A town grown
      // from 5 to 7 afterwards kept the stale 5-role pick, which still passed
      // the assign guard (5 <= 7) and dealt 5 of 7 seats, silently short.
      // These two track WHEN the current selection was made and whether the
      // storyteller has touched it since, so opening the modal can refresh
      // the pick without stomping deliberate hand edits.
      handEdited: false,
      pickedFor: 0
    };
  },
  computed: {
    /** How many characters are picked, all teams together. */
    selectedRoles() {
      return Object.values(this.roleSelection).reduce(
        (sum, list) => sum + this.pickedIn(list),
        0,
      );
    },
    /** Is any picked character one that alters the game's setup? */
    setupInPlay() {
      return Object.values(this.roleSelection).some((list) =>
        list.some((role) => role.selected && role.setup),
      );
    },
    /**
     * FT-946: THE list this modal shows — `roleSelection` itself minus
     * whatever is already seated, unless Duplicates is on. `roleSelection`
     * stays intact underneath (so a role's `.selected` count survives a
     * Duplicates toggle instead of the whole picker rebuilding), only the
     * RENDERED list — and so what a click can reach — excludes an in-play
     * role entirely. RoleDrawer's own list only GREYS an in-play role out
     * (it doubles as a seating chart, so it keeps every row); this picker
     * exists purely to build a random pool, so there is nothing to keep an
     * already-seated role visible for.
     */
    visibleRoleSelection: function() {
      if (this.allowDup) return this.roleSelection;
      const visible = {};
      Object.keys(this.roleSelection).forEach((team) => {
        visible[team] = this.roleSelection[team].filter(
          (role) => !placedCount(role, this.players),
        );
      });
      return visible;
    },
    /** THE global Duplicates switch (store/index.js) — the same one
     *  RoleDrawer's "Dupes" checkbox and RoleActions' chip read and write,
     *  so toggling it here toggles it everywhere. Replaces this modal's own
     *  disconnected `allowMultiple` flag, which controlled the same idea
     *  (more than one of a role in the random pool) without ever agreeing
     *  with the app's one real Duplicates setting. */
    allowDup: {
      get() {
        return this.$store.state.allowDupRoles;
      },
      set(on) {
        this.$store.commit("setAllowDupRoles", on);
      }
    },
    ...mapState(["roles", "modals"]),
    ...mapState("players", ["players"]),
    ...mapGetters({ nonTravelers: "players/nonTravelers" }),
  },
  methods: {
    /** The picked count within one team's list. */
    pickedIn(list) {
      return list.reduce((sum, role) => sum + (role.selected || 0), 0);
    },
    /** How many of this team the current town size calls for. */
    neededFor(team) {
      const row = this.game[Math.max(5, this.nonTravelers) - 5];
      return (row && row[team]) || 0;
    },
    // FT-1394: hand edits flow through these two so the modal knows the
    // storyteller has shaped the selection (a fresh auto-pick on open would
    // otherwise throw their choices away).
    toggleRole(role) {
      role.selected = role.selected ? 0 : 1;
      this.handEdited = true;
    },
    nudgeRole(role, delta) {
      role.selected += delta;
      this.handEdited = true;
    },
    /** Start over: clear every pick, then draw the town size's composition
     *  at random from the script. */
    selectRandomRoles() {
      const byTeam = {};
      this.roles.forEach((role) => {
        // zeroed BEFORE the lists are handed to Vue below, so the count is
        // observed along with everything else on the role
        this.$set(role, "selected", 0);
        if (role.team === "traveler") return;
        if (!byTeam[role.team]) byTeam[role.team] = [];
        byTeam[role.team].push(role);
      });
      this.roleSelection = byTeam;

      const playerCount = Math.max(5, this.nonTravelers);
      const shape = this.game[playerCount - 5];
      for (const team in shape) {
        for (let left = shape[team]; left > 0; left--) {
          if (this.roleSelection[team]) {
            // FT-946: the default auto-pick never reaches for a role
            // already seated unless Duplicates is on — this is the pool
            // "Assign N characters randomly" draws from, so an in-play role
            // slipping in here is exactly the silent double-assign the
            // picker is supposed to forbid.
            const pool = this.roleSelection[team].filter(
              (role) =>
                !role.selected &&
                (this.allowDup || !placedCount(role, this.players)),
            );
            if (pool.length) anyOf(pool).selected = 1;
          }
        }
      }
      // FT-1394: a fresh auto-pick supersedes any hand edits and is stamped
      // with the seat count it was built for.
      this.handEdited = false;
      this.pickedFor = playerCount;
    },
    /** Hand the picked characters out to the seats in a random order. */
    assignRoles() {
      // FT-1387: the button greys but the click still lands here — refusing
      // (too many selected, or none) used to be silent. Say the counts.
      if (this.selectedRoles > this.nonTravelers || !this.selectedRoles) {
        flashHint(
          `Select ${this.nonTravelers} characters — you have ${this.selectedRoles}.`,
        );
        return;
      }
      // one card per copy picked
      const deck = [];
      Object.values(this.roleSelection).forEach((list) => {
        list.forEach((role) => {
          for (let copy = 0; copy < role.selected; copy++) deck.push(role);
        });
      });
      // Fisher–Yates: every order equally likely
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }
      // travellers keep their seat's role; once the deck is spent, so does
      // everyone after
      this.players.forEach((player) => {
        if (!deck.length || player.role.team === "traveler") return;
        this.$store.commit("players/update", {
          player,
          property: "role",
          value: deck.pop(),
        });
      });
      this.toggleModal("roles");
    },
    ...mapMutations(["toggleModal"]),
  },
  mounted() {
    if (!Object.keys(this.roleSelection).length) this.selectRandomRoles();
  },
  watch: {
    // a different script — the old pick means nothing now
    roles() {
      this.selectRandomRoles();
    },
    /**
     * FT-1394: refresh the auto-pick when the modal OPENS, not just once at
     * mount — the mount-time pick froze the composition at the load-time
     * seat count (grow 5 → 7 and the stale 5-role pick still passed the
     * assign guard, dealing 7 seats only 5 roles). The pick is kept ONLY
     * when the storyteller hand-edited it AND the seat count it was built
     * for still matches; a seat-count change always re-picks, because a
     * selection sized for a different town is exactly the stale state this
     * fix exists to clear.
     */
    "modals.roles"(open) {
      if (!open) return;
      if (
        !this.handEdited ||
        this.pickedFor !== Math.max(5, this.nonTravelers)
      ) {
        this.selectRandomRoles();
      }
    },
    /**
     * FT-1394: the town can grow or shrink while the modal stands open (a
     * player joins mid-setup) — the composition target just changed, so
     * re-pick even over hand edits; the counts row is already showing the
     * new totals.
     */
    nonTravelers() {
      if (this.modals.roles) {
        this.selectRandomRoles();
      }
    },
    /**
     * FT-946: turning Duplicates OFF can leave a role selected from while it
     * was on — that selection would otherwise survive into `assignRoles`'s
     * pool even though the role is now hidden and unreachable by a click,
     * which is the same silent double-assign this whole fix exists to close.
     * Clears just that role's own count; every other selection is untouched.
     */
    allowDup(on) {
      if (on) return;
      Object.values(this.roleSelection).forEach((teamRoles) => {
        teamRoles.forEach((role) => {
          if (role.selected && placedCount(role, this.players)) {
            this.$set(role, "selected", 0);
          }
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../vars.scss";

/* FT-1242: the assign button's baked dealing hand, sized to its text line. */
.rm-deal-mark {
  width: 15px;
  height: 15px;
  object-fit: contain;
  vertical-align: -2px;
}

// A dozen-odd coins to a row on a desktop plate; each team's row keeps a
// gutter on its left where its tally sits.
$coin: 4.2vw;
$gutter: 3.6vw;

.tokens {
  padding-left: $gutter;
}

.coin {
  width: $coin;
  height: $coin;
  margin: 0.3vw;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.45;
  transition:
    opacity 180ms ease,
    transform 180ms ease;

  // the glow rides the coin itself, not this item: a filter here would make
  // the item its own stacking layer and bury the count control under the
  // next row's coins
  @each $team, $color in $team-tokens {
    &.team-#{$team} .token {
      filter: drop-shadow(0 0 0.4vw rgba($color, 0.75));
    }
  }

  &:hover {
    transform: scale(1.3);
    z-index: 4;
  }

  &.picked {
    opacity: 1;
    .buttons {
      display: flex;
    }
    .setup-flag {
      display: block;
    }
  }
}

// this coin changes the setup
.setup-flag {
  display: none;
  position: absolute;
  top: 0;
  right: 0;
  font-size: 130%;
  color: red;
  filter: drop-shadow(0 1px 2px #000);
}

// − ×N + under a picked coin, when duplicates are on
.buttons {
  display: none;
  position: absolute;
  top: 92%;
  left: 0;
  width: 100%;
  align-items: center;
  z-index: 3;
  font-weight: bold;
  filter: drop-shadow(0 1px 2px #000);

  .count {
    flex: 1;
    text-align: center;
  }

  svg {
    opacity: 0.35;
    cursor: pointer;
    transition:
      opacity 150ms,
      color 150ms;
    &:hover {
      opacity: 1;
      color: red;
    }
  }
}

// picked / needed, in the team's own colour
.team-tally {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: $gutter;
  height: $gutter;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 90%;
  font-weight: bold;
  @include team-ink(22%);
}

// on a phone a vw-sized coin is a speck; fewer, bigger ones
@media (max-width: 640px) {
  .tokens {
    padding-left: 10vw;
  }
  .coin {
    width: 12vw;
    height: 12vw;
    margin: 1vw;
  }
  .team-tally {
    width: 10vw;
    height: 10vw;
  }
}

// the dialog's own controls, kept inside this dialog's plate
.roles .modal {
  .multiple {
    display: block;
    text-align: center;
    cursor: pointer;
    transition: color 150ms;
    &:hover,
    &.checked {
      color: red;
    }
    &.checked {
      margin-top: 0.6em;
    }
    svg {
      margin-right: 0.35em;
    }
    input {
      display: none;
    }
  }

  .setup-note {
    position: absolute;
    right: 16px;
    bottom: 12px;
    z-index: 5;
    color: red;
    cursor: help;

    > svg {
      font-size: 1.6em;
      vertical-align: middle;
    }

    .setup-tip {
      display: none;
      position: absolute;
      right: 0;
      bottom: calc(100% + 6px);
      width: 240px;
      padding: 6px 10px;
      border: 2px solid #000;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.85);
      color: #fff;
      font-size: 85%;
      line-height: 1.35;
      text-align: center;
    }

    &:hover .setup-tip {
      display: block;
    }
  }
}
</style>
